// assets/byte-free-gift.js

(() => {
  const configElement = document.getElementById("ByteFreeGiftConfig");
  if (!configElement) return;

  const giftConfig = JSON.parse(configElement.textContent);
  const GIFT_PROPERTY_KEY = "_byte_free_gift";
  const CART_MUTATION_PATHS = [
    "/cart/add",
    "/cart/change",
    "/cart/update",
    "/cart/clear",
  ];
  const rootUrl = giftConfig.rootUrl.endsWith("/")
    ? giftConfig.rootUrl
    : `${giftConfig.rootUrl}/`;
  const nativeFetch = window.fetch.bind(window);
  // Checkout controls in the cart drawer and cart page (incl. mobile CTA and accelerated checkout)
  const CHECKOUT_SELECTOR =
    'button[name="checkout"], .cart-summary__payment-buttons';
  // The cart page removes lines with a native form submit, so no fetch is seen there
  const LINE_REMOVAL_SELECTOR =
    "[data-remove-item], [data-remove-line], [data-qty-minus], [data-qty-decrease]";
  const LOCK_ATTRIBUTE = "data-gift-locked";

  let isSyncing = false;
  let hasPendingSync = false;
  let pendingMutationCount = 0;
  let isCartMutating = false;
  let isGiftOnlyCart = false;

  function getRequestPathname(resource) {
    const requestUrl = typeof resource === "string" ? resource : resource?.url;
    if (!requestUrl) return "";

    return new URL(requestUrl, window.location.origin).pathname;
  }

  function isCartMutationRequest(resource) {
    const requestPathname = getRequestPathname(resource);

    return CART_MUTATION_PATHS.some((cartPath) =>
      requestPathname.includes(cartPath),
    );
  }

  function isGiftLine(lineItem) {
    return Boolean(lineItem.properties?.[GIFT_PROPERTY_KEY]);
  }

  function isQualifyingLine(lineItem) {
    if (isGiftLine(lineItem)) return false;

    return lineItem.product_id !== giftConfig.productId;
  }

  async function requestCart(endpoint, payload) {
    const requestOptions = payload
      ? {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      : { headers: { Accept: "application/json" } };

    const response = await nativeFetch(`${rootUrl}${endpoint}`, requestOptions);

    if (!response.ok) {
      throw new Error(`Free gift cart request failed: ${response.status}`);
    }

    return response.json();
  }

  function setCheckoutLocked(isLocked) {
    let hasUnlocked = false;

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((checkoutElement) => {
      if (isLocked) {
        checkoutElement.setAttribute(LOCK_ATTRIBUTE, "");
        checkoutElement.setAttribute("aria-disabled", "true");
        checkoutElement.disabled = true;
        return;
      }

      if (!checkoutElement.hasAttribute(LOCK_ATTRIBUTE)) return;

      checkoutElement.removeAttribute(LOCK_ATTRIBUTE);
      checkoutElement.setAttribute("aria-disabled", "false");
      checkoutElement.disabled = false;
      hasUnlocked = true;
    });

    // Let the theme's agreement checkbox re-apply its own disabled state
    if (hasUnlocked) {
      document.querySelectorAll("[data-agreement-check]").forEach((checkbox) => {
        checkbox.dispatchEvent(new Event("change"));
      });
    }
  }

  function refreshCheckoutLock() {
    setCheckoutLocked(isCartMutating || isGiftOnlyCart);
  }

  // Checkout stays locked from the moment a cart change starts until the gift sync settles,
  // so a cart left with only the gift line can't reach checkout while the gift is being removed
  function lockCheckoutForMutation() {
    isCartMutating = true;
    refreshCheckoutLock();
  }

  async function syncGiftLine() {
    const cart = await requestCart("cart.js");
    const giftLine = cart.items.find(isGiftLine);
    const hasQualifyingItem = cart.items.some(isQualifyingLine);

    isGiftOnlyCart = Boolean(giftLine) && !hasQualifyingItem;
    refreshCheckoutLock();

    if (hasQualifyingItem && !giftLine) {
      await requestCart("cart/add.js", {
        items: [
          {
            id: giftConfig.variantId,
            quantity: 1,
            properties: { [GIFT_PROPERTY_KEY]: "true" },
          },
        ],
      });
      return true;
    }

    if (giftLine && !hasQualifyingItem) {
      await requestCart("cart/change.js", { id: giftLine.key, quantity: 0 });
      isGiftOnlyCart = false;
      return true;
    }

    if (giftLine && giftLine.quantity !== 1) {
      await requestCart("cart/change.js", { id: giftLine.key, quantity: 1 });
      return true;
    }

    return false;
  }

  async function handleCartChange() {
    if (isSyncing) {
      hasPendingSync = true;
      return;
    }

    isSyncing = true;

    try {
      const hasCartChanged = await syncGiftLine();

      if (hasCartChanged) {
        notifyCartChanged();
      }
    } catch (error) {
      console.error(error);
    } finally {
      isSyncing = false;

      if (hasPendingSync) {
        hasPendingSync = false;
        handleCartChange();
      } else if (pendingMutationCount === 0) {
        isCartMutating = false;
        refreshCheckoutLock();
      }
    }
  }

  function notifyCartChanged() {
    const isCartPage = window.location.pathname.startsWith(giftConfig.cartUrl);

    if (isCartPage) {
      window.location.reload();
      return;
    }

    // Dost theme's CartDrawer listens to this event and re-fetches /cart.js
    document.dispatchEvent(new CustomEvent("cart:updated"));
  }

  window.fetch = async (resource, requestOptions) => {
    if (!isCartMutationRequest(resource)) {
      return nativeFetch(resource, requestOptions);
    }

    pendingMutationCount += 1;
    lockCheckoutForMutation();

    try {
      return await nativeFetch(resource, requestOptions);
    } finally {
      pendingMutationCount -= 1;
      // Let the theme finish its own cart render before the gift sync runs
      window.setTimeout(handleCartChange, 0);
    }
  };

  document.addEventListener(
    "click",
    (event) => {
      if (event.target.closest?.(LINE_REMOVAL_SELECTOR)) {
        lockCheckoutForMutation();
      }
    },
    true,
  );

  // Safety net for a checkout submit that lands before the disabled state is applied
  document.addEventListener(
    "submit",
    (event) => {
      const isCheckoutSubmit = event.submitter?.name === "checkout";

      if (isCheckoutSubmit && (isCartMutating || isGiftOnlyCart)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  handleCartChange();
})();
